import { useRef, useEffect } from 'react';
import type { Workflow } from '@n8n-builder/n8n-types';

function LogoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 28 28" fill="none">
      <rect width="28" height="28" rx="7" fill="#0072FF" />
      <path d="M7.5 14h6m0 0l5.5-5m-5.5 5l5.5 5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
import { MessageBubble } from './MessageBubble';
import { StreamingStatus } from './StreamingStatus';
import { PlanCard } from './PlanCard';
import { QuestionCard } from './QuestionCard';
import { CredentialStatusCard } from './CredentialStatusCard';
import type { ToolCall, WorkflowPlan, ChatPhase, UserQuestions } from '../../store/chat';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  workflow?: unknown;
  workflowId?: string;
  plan?: WorkflowPlan;
}

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  statusMessage: string | null;
  streamingToolCalls: ToolCall[];
  currentPlan: WorkflowPlan | null;
  currentQuestions: UserQuestions | null;
  currentPhase: ChatPhase;
  onBuild: () => void;
  onAnswerQuestions: (answers: Record<string, string>) => void;
  isBuilding: boolean;
  isAnswering: boolean;
  sidePanelOpen?: boolean;
  showCredentialCard?: boolean;
  currentWorkflow?: Workflow | null;
  currentWorkflowId?: string;
  onDismissCredentialCard?: () => void;
}

export function ChatMessages({
  messages,
  isLoading,
  statusMessage,
  streamingToolCalls,
  currentPlan,
  currentQuestions,
  currentPhase,
  onBuild,
  onAnswerQuestions,
  isBuilding,
  isAnswering,
  sidePanelOpen = false,
  showCredentialCard = false,
  currentWorkflow,
  currentWorkflowId,
  onDismissCredentialCard,
}: ChatMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, streamingToolCalls, currentPlan, currentQuestions, showCredentialCard]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LogoIcon className="mb-6 h-14 w-14" />
        <h2 className="text-xl font-semibold text-gray-900">
          Build any n8n workflow with AI
        </h2>
        <p className="mt-2 max-w-sm text-center text-gray-500">
          Describe what you want to automate and get a ready-to-deploy workflow in seconds.
        </p>
      </div>
    );
  }

  // Check if the current workflow has already been built (plan was approved)
  const hasBuiltWorkflow = messages.some(m => m.workflow);

  // Use wider layout when side panel is collapsed
  const maxWidthClass = sidePanelOpen ? 'max-w-4xl' : 'max-w-5xl';

  return (
    <div className={`mx-auto w-full ${maxWidthClass} space-y-6`}>
      {messages
        .filter((m) => m.role === 'user' || m.content.trim() || m.workflow || m.plan)
        .map((message, index) => (
          <div key={index}>
            {/* Only render the bubble if there's content to show */}
            {(message.content.trim() || !!message.workflow) && (
              <MessageBubble
                role={message.role}
                content={message.content}
              />
            )}
            {/* Render PlanCard inline after its owning message */}
            {message.plan && (
              <div>
                <PlanCard
                  plan={message.plan}
                  onBuild={onBuild}
                  isBuilding={isBuilding}
                  isBuilt={hasBuiltWorkflow || isBuilding}
                />
              </div>
            )}
          </div>
        ))}

      {/* Streaming status — inline dots + rolling text */}
      {(isLoading || isBuilding) && !(currentPlan && !messages.some(m => m.plan)) && (
        <div className="animate-fade-in">
          <StreamingStatus
            phase={isBuilding ? 'building' : 'planning'}
            toolCalls={streamingToolCalls}
            statusMessage={statusMessage}
          />
        </div>
      )}

      {/* Live Plan Card fallback — shown when currentPlan exists via SSE
          but hasn't been attached to a message yet */}
      {currentPlan && !messages.some(m => m.plan) && (
        <div className="animate-fade-in">
          <PlanCard
            plan={currentPlan}
            onBuild={onBuild}
            isBuilding={isBuilding}
            isBuilt={hasBuiltWorkflow || isBuilding}
          />
        </div>
      )}

      {/* Question Card - shown during answering phase */}
      {currentQuestions && currentPhase === 'answering' && (
        <div className="animate-fade-in">
          <QuestionCard
            questions={currentQuestions}
            onAnswer={onAnswerQuestions}
            isSubmitting={isAnswering}
          />
        </div>
      )}

      {/* Inline Credential Status Card - shown after build completes */}
      {showCredentialCard && currentWorkflow && onDismissCredentialCard && (
        <CredentialStatusCard
          workflow={currentWorkflow}
          workflowId={currentWorkflowId}
          onDismiss={onDismissCredentialCard}
        />
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}
